import { useMemo } from 'react';
import { useAuth } from 'src/contexts/AuthContext';
import type {
  UserRole,
  RoleProtectionConfig,
  RoleCheckResult
} from 'src/types/auth';

/**
 * Custom hook for role-based permission checking
 *
 * Provides a comprehensive API for checking user roles and permissions
 * with memoization for performance optimization.
 *
 * @param config Optional role protection configuration
 * @returns RoleCheckResult with permission checking utilities
 *
 * @example
 * const { hasPermission, isAdmin, hasRole } = useRoleCheck({
 *   allowedRoles: ['ADMIN', 'DISPATCHER']
 * });
 *
 * if (hasPermission) {
 *   // Render admin content
 * }
 */
export const useRoleCheck = (
  config: RoleProtectionConfig = {}
): RoleCheckResult => {
  const { user, isAuthenticated } = useAuth();
  const { allowedRoles = [], blockedRoles = [], requireAll = false } = config;

  return useMemo(() => {
    // If not authenticated, deny all permissions
    if (!isAuthenticated() || !user) {
      return {
        hasPermission: false,
        hasRole: () => false,
        hasAnyRole: () => false,
        hasAllRoles: () => false,
        userRoles: [],
        isAdmin: false,
        isDispatcher: false,
        isCompanyAdmin: false
      };
    }

    const userRoles: UserRole[] = (user.roles as UserRole[]) || [];

    /**
     * Check if user has a specific role
     */
    const hasRole = (role: UserRole): boolean => userRoles.includes(role);

    /**
     * Check if user has any of the provided roles
     */
    const hasAnyRole = (roles: UserRole[]): boolean =>
      roles.some((role) => userRoles.includes(role));

    /**
     * Check if user has all of the provided roles
     */
    const hasAllRoles = (roles: UserRole[]): boolean =>
      roles.every((role) => userRoles.includes(role));

    /**
     * Calculate permission based on configuration
     */
    const calculatePermission = (): boolean => {
      // Check blocked roles first (highest precedence)
      if (blockedRoles.length > 0) {
        const hasBlockedRole = blockedRoles.some((role) =>
          userRoles.includes(role)
        );
        if (hasBlockedRole) {
          return false;
        }
      }

      // Check allowed roles
      if (allowedRoles.length > 0) {
        return requireAll
          ? allowedRoles.every((role) => userRoles.includes(role))
          : allowedRoles.some((role) => userRoles.includes(role));
      }

      // If no specific restrictions, permission is granted
      return true;
    };

    // Convenience flags for common role checks
    const isAdmin = hasRole('ADMIN');
    const isDispatcher = hasRole('DISPATCHER');
    const isCompanyAdmin = hasAnyRole(['ADMIN', 'EVU_ADMIN', 'EIU_ADMIN']);

    return {
      hasPermission: calculatePermission(),
      hasRole,
      hasAnyRole,
      hasAllRoles,
      userRoles,
      isAdmin,
      isDispatcher,
      isCompanyAdmin
    };
  }, [user, isAuthenticated, allowedRoles, blockedRoles, requireAll]);
};

export default useRoleCheck;
